import React, { createContext, useEffect, useState } from "react";

export const OpenPayContext = createContext({
  isReady: false,
  deviceSessionId: null,
  error: null,
});

const sandboxModeRaw = process.env.REACT_APP_OPENPAY_SANDBOX_MODE;
const sandboxModeBoolean = sandboxModeRaw === 'true';

if (typeof window !== 'undefined' && !sandboxModeBoolean) {
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string' && url.includes('sandbox.openpay.js.openpay.com')) {
      return Promise.reject(new Error('Bloqueado'));
    }
    return originalFetch.apply(this, args);
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    if (typeof url === 'string' && url.includes('sandbox.openpay.js.openpay.com')) {
      return;
    }
    return originalXHROpen.call(this, method, url, ...rest);
  };

  const originalCreateElement = document.createElement;
  document.createElement = function(tagName) {
    const element = originalCreateElement.call(document, tagName);
    if (tagName.toLowerCase() === 'script') {
      const originalSetAttribute = element.setAttribute;
      element.setAttribute = function(name, value) {
        if (name === 'src' && typeof value === 'string' && value.includes('sandbox.openpay.js.openpay.com')) {
          return;
        }
        return originalSetAttribute.call(this, name, value);
      };
      Object.defineProperty(element, 'src', {
        set: function(value) {
          if (typeof value === 'string' && value.includes('sandbox.openpay.js.openpay.com')) {
            return;
          }
          originalSetAttribute.call(this, 'src', value);
        }
      });
    }
    return element;
  };
}

const OpenPayProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [deviceSessionId, setDeviceSessionId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadScript = (src, id) =>
      new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.id = id;
        script.async = false;

        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));

        document.head.appendChild(script);
      });

    const initOpenPay = async () => {
      try {
        await loadScript(
          "https://openpay.s3.amazonaws.com/openpay.v1.min.js",
          "openpay-js"
        );
        await loadScript(
          "https://openpay.s3.amazonaws.com/openpay-data.v1.min.js",
          "openpay-data-js"
        );
        
        let retries = 0;
        while (!window.OpenPay && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!window.OpenPay || !window.OpenPay.deviceData) {
          throw new Error("OpenPay no disponible después de esperar");
        }

        const merchantId = process.env.REACT_APP_OPENPAY_MERCHANT_ID;
        const apiKey = process.env.REACT_APP_OPENPAY_PUBLIC_KEY;
        
        window.OpenPay.setId(merchantId);
        window.OpenPay.setApiKey(apiKey);
        window.OpenPay.setSandboxMode(sandboxModeBoolean);
        
        const deviceId = window.OpenPay.deviceData.setup();
        
        if (!deviceId) {
          throw new Error("No se pudo generar device_session_id");
        }
        
        setDeviceSessionId(deviceId);
        setIsReady(true);
        
      } catch (err) {
        setError(err.message);
      }
    };

    initOpenPay();
  }, []);

  return (
    <OpenPayContext.Provider value={{ isReady, deviceSessionId, error }}>
      {children}
    </OpenPayContext.Provider>
  );
};

export default OpenPayProvider;